// Create character view - multi-step character creation wizard

import { css, html } from 'lit';
import { api, getCDNBaseUrl } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { ALL_SKILLS, FANTASY_FIRST_NAMES } from '../../lib/constants.js';
import {
  getChoiceLabel,
  getSelectionType,
  isOriginFeatChoice,
  isSpellListChoice,
  parseCostToCopper,
} from '../../lib/format.js';
import { router } from '../../lib/router.js';
import '../shared/ability-score-assigner.js';
import '../shared/equipment-step.js';
import '../shared/background-step.js';
import '../shared/species-step.js';
import '../shared/class-step.js';
import '../shared/alignment-step.js';
import '../shared/details-step.js';
import {
  computeCurrentGoldCopper,
  computeCurrentInventory,
  computeStartingGoldCopper,
} from '../../lib/equipment-utils.js';

/**
 * @typedef {'str' | 'dex' | 'con' | 'int' | 'wis' | 'cha'} AbilityKey
 */

/**
 * @typedef {object} ClassListItem
 * @property {string} slug
 * @property {string} name
 * @property {string} [description]
 * @property {string} [primaryAbility]
 * @property {string} [likes]
 * @property {string} [complexity]
 */

/**
 * @typedef {object} BackgroundListItem
 * @property {string} slug
 * @property {string} name
 * @property {string} [description]
 * @property {Array<string>} [abilityScores]
 */

/**
 * @typedef {object} SpeciesListItem
 * @property {string} slug
 * @property {string} name
 * @property {string} [description]
 */

/**
 * @typedef {object} SkillOption
 * @property {string} slug
 * @property {string} [name]
 * @property {string} [ability]
 * @property {boolean} [recommended]
 */

/**
 * @typedef {object} SkillProficiencies
 * @property {number} count
 * @property {Array<SkillOption>} options
 * @property {Array<string>} [recommended]
 */

/**
 * @typedef {object} CoreTraits
 * @property {string} primaryAbility
 * @property {number} hitDie
 * @property {Array<string>} savingThrowProficiencies
 * @property {Array<string>} [weaponProficiencies]
 * @property {Array<string>} [armorTraining]
 */

/** @typedef {import('../../types/jsdoc-types.js').ChoiceOption} ChoiceOption */
/** @typedef {import('../../types/jsdoc-types.js').FeatureChoice} FeatureChoice */

/**
 * @typedef {object} ClassFeature
 * @property {string} slug
 * @property {string} name
 * @property {string} [description]
 * @property {Array<FeatureChoice>} [choices]
 */

/**
 * @typedef {object} EquipmentPackage
 * @property {string} slug
 * @property {string} name
 * @property {string} [description]
 * @property {number} [gold]
 * @property {number} [silver]
 * @property {number} [copper]
 * @property {Array<EquipmentItem>} [items]
 */

/**
 * @typedef {object} EquipmentItem
 * @property {string} slug
 * @property {string} name
 * @property {string} cost
 * @property {number} quantity
 * @property {string} category
 * @property {string} [description]
 * @property {number} [weight]
 * @property {boolean} [bundlePricing]
 * @property {number} [bundleSize]
 * @property {{category: string, attackType: string, damage: {dice: string, type: string}, mastery?: string, properties?: {[key: string]: boolean|string|{normal?: number, long?: number}}}} [weapon]
 * @property {{category: string, ac: {base?: number, add?: number, addDexModifier?: boolean, maxDexModifier?: number}, stealthDisadvantage?: boolean, minStr?: number}} [armor]
 */

/**
 * @typedef {object} ClassCreationOptions
 * @property {{name: string, defaultAbilityScores?: {str: number, dex: number, con: number, int: number, wis: number, cha: number}, recommendedBackground?: string, recommendedAlignment?: string}} class
 * @property {CoreTraits} coreTraits
 * @property {SkillProficiencies} [skillProficiencies]
 * @property {Array<EquipmentPackage>} [equipmentPackages]
 * @property {Array<ClassFeature>} [features]
 */

/**
 * @typedef {object} BackgroundToolOptions
 * @property {string} type
 * @property {Array<string>} [options]
 * @property {Array<string>} [tools]
 */

/**
 * @typedef {object} BackgroundCreationOptions
 * @property {{name: string}} [background]
 * @property {Array<EquipmentPackage>} [equipmentPackages]
 * @property {BackgroundToolOptions} [tools]
 * @property {{abilities: Array<string>}} [abilityScores]
 * @property {{slug: string, name?: string, description?: string, prefilledChoices?: {[key: string]: string}}} [feat]
 * @property {{skills: Array<string>, count?: number, options?: Array<SkillOption>}} [skills]
 */

/**
 * @typedef {object} SpeciesInfo
 * @property {string} creatureType
 * @property {Array<string>} size
 * @property {number} speed
 * @property {Array<string>} [recommendedLanguages]
 */

/**
 * @typedef {object} SpecialTrait
 * @property {string} name
 * @property {string} description
 * @property {Array<FeatureChoice>} [choices]
 */

/**
 * @typedef {object} LineageData
 * @property {string} [name]
 * @property {string} [description]
 * @property {Array<{name: string, description: string}>} [benefits]
 * @property {Array<FeatureChoice>} [choices]
 */

/**
 * @typedef {object} SpeciesCreationOptions
 * @property {SpeciesInfo} species
 * @property {{[key: string]: LineageData}} [lineages]
 * @property {Array<SpecialTrait>} [specialTraits]
 */

/**
 * @typedef {object} FeatCreationChoices
 * @property {Array<FeatureChoice>} [choices]
 * @property {{name?: string, description?: string}} [feat]
 * @property {{[classSlug: string]: {[choiceId: string]: string}}} [recommendedByClass]
 */

/**
 * @typedef {object} OriginFeatItem
 * @property {string} slug
 * @property {string} [name]
 * @property {string} [description]
 * @property {string} [category]
 */

/**
 * @typedef {object} TokenListItem
 * @property {string} slug
 * @property {string} name
 * @property {string} [image_url]
 */

/**
 * @typedef {object} CharacterFormData
 * @property {string} name
 * @property {string} classSlug
 * @property {string} backgroundSlug
 * @property {string} speciesSlug
 * @property {string} alignment
 * @property {{[key in AbilityKey]: number}} abilityScores
 * @property {string} abilityScoreAdjustmentMethod
 * @property {string} abilityScoreAdjustmentPrimary
 * @property {string} abilityScoreAdjustmentSecondary
 * @property {Array<string>} skillProficiencies
 * @property {{[key: string]: Array<string>}} featureChoices
 * @property {Array<string>} languages
 * @property {string} equipmentPackageId
 * @property {string} tokenId
 * @property {string} backgroundEquipmentPackageId
 * @property {string} backgroundToolChoice
 * @property {{[key: string]: string}} featChoices
 * @property {Array<string>} replacementSkills
 * @property {string} speciesSize
 * @property {string} speciesLineage
 * @property {{[key: string]: string}} speciesTraitChoices
 * @property {{[key: string]: string}} originFeatChoices
 * @property {{soldItems: Array<{slug: string, name?: string, cost?: string, quantity: number}>, purchasedItems: Array<{slug: string, name?: string, cost?: string, quantity: number}>}} equipmentChanges
 */

// Steps configuration
const STEPS = [
  { id: 'class', label: 'Class' },
  { id: 'background', label: 'Background' },
  { id: 'species', label: 'Species' },
  { id: 'abilities', label: 'Abilities' },
  { id: 'equipment', label: 'Equipment' },
  { id: 'alignment', label: 'Alignment' },
  { id: 'details', label: 'Details' },
];

const STORAGE_KEY = 'rpgish:character-creation';

/**
 * Create character view component
 * Multi-step wizard for creating new characters
 */
class CreateCharacterView extends BaseComponent {
  static properties = {
    _currentStep: { type: Number, state: true },
    _loading: { type: Boolean, state: true },
    _error: { type: String, state: true },
    _creating: { type: Boolean, state: true },
    _createError: { type: String, state: true },
    // Data from API
    _classes: { type: Array, state: true },
    _classOptions: { type: Object, state: true },
    _backgrounds: { type: Array, state: true },
    _backgroundOptions: { type: Object, state: true },
    _featOptions: { type: Object, state: true },
    _species: { type: Array, state: true },
    _speciesOptions: { type: Object, state: true },
    // TODO: Move _tokens and _tokensLoading into details-step — only used there, parent just passes through
    _tokens: { type: Array, state: true },
    _tokensLoading: { type: Boolean, state: true },
    // TODO: Move _equipment and _equipmentLoading into equipment-step — only used there, parent just passes through
    _equipment: { type: Array, state: true },
    _equipmentLoading: { type: Boolean, state: true },
    // TODO: Move _originFeats and _originFeatOptions into species-step — only used there, parent just passes through
    _originFeats: { type: Array, state: true },
    _originFeatOptions: { type: Object, state: true },
    // UI state
    // TODO: Move sticky header detection into each step component so they manage their own IntersectionObserver/scroll listener internally, eliminating this prop
    _isHeaderStuck: { type: Boolean, state: true },
    // Form data
    _formData: { type: Object, state: true },
  };

  static styles = css`
    :host {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-width: 0;
    }

    .step-indicator {
      padding: 8px 16px;
      border-radius: 20px;
      background: #e9ecef;
      color: #6c757d;
      font-size: 0.875rem;
      font-weight: 500;
      white-space: nowrap;
    }

    .step-indicator.active {
      background: #0d6efd;
      color: white;
    }

    .step-indicator.completed {
      background: #198754;
      color: white;
    }

    .step-connector {
      width: 20px;
      height: 2px;
      background: #dee2e6;
    }

    .step-connector.completed {
      background: #198754;
    }

    .character-creation-footer {
      margin-top: auto;
      position: sticky;
      bottom: 0;
      background-color: rgba(255, 255, 255, 0.5);
      backdrop-filter: blur(8px);
      border-top: 1px solid rgba(0, 0, 0, 0.125);
      padding: 1rem 1.25rem;
      z-index: 100;
    }

    .character-creation-footer .btn {
      background-color: white;
    }
    .character-creation-footer .btn-primary,
    .character-creation-footer .btn-success {
      background-color: var(--bs-btn-bg);
    }
    .character-creation-footer .btn-outline-primary,
    .character-creation-footer .btn-outline-secondary {
      background-color: white;
    }

    .header-sentinel {
      height: 1px;
      margin-bottom: -1px;
    }

    .sticky-header {
      position: sticky;
      top: 0;
      background-color: rgba(255, 255, 255, 0.5);
      backdrop-filter: blur(8px);
      z-index: 10;
      padding-top: 0.5rem;
      padding-bottom: 1rem;
      margin-top: -0.5rem;
      margin-left: -1rem;
      margin-right: -1rem;
      padding-left: 1rem;
      padding-right: 1rem;
      border-bottom: 1px solid transparent;
      transition: border-color 0.15s ease;
    }

    .sticky-header.stuck {
      border-bottom-color: rgba(0, 0, 0, 0.125);
      padding-top: 1rem;
    }

    .sticky-header .btn {
      background-color: white;
    }
    .sticky-header .btn-primary,
    .sticky-header .btn-success {
      background-color: var(--bs-btn-bg);
    }
    .sticky-header .btn-outline-primary,
    .sticky-header .btn-outline-secondary {
      background-color: white;
    }

    .check-mark {
      color: white;
    }
  `;

  constructor() {
    super();
    this._currentStep = 0;
    this._loading = true;
    /** @type {string | null} */
    this._error = null;
    this._creating = false;
    /** @type {string | null} */
    this._createError = null;
    /** @type {Array<ClassListItem>} */
    this._classes = [];
    /** @type {ClassCreationOptions | null} */
    this._classOptions = null;
    /** @type {Array<BackgroundListItem>} */
    this._backgrounds = [];
    /** @type {BackgroundCreationOptions | null} */
    this._backgroundOptions = null;
    /** @type {FeatCreationChoices | null} */
    this._featOptions = null;
    /** @type {Array<SpeciesListItem>} */
    this._species = [];
    /** @type {SpeciesCreationOptions | null} */
    this._speciesOptions = null;
    /** @type {Array<TokenListItem>} */
    this._tokens = [];
    this._tokensLoading = false;
    /** @type {Array<EquipmentItem>} */
    this._equipment = [];
    this._equipmentLoading = false;
    /** @type {Array<OriginFeatItem>} */
    this._originFeats = [];
    /** @type {FeatCreationChoices | null} */
    this._originFeatOptions = null;
    this._isHeaderStuck = false;
    /** @type {HTMLElement | null} */
    this._scrollContainer = null;
    /** @type {EventListener | null} */
    this._scrollHandler = null;
    /** @type {Element | null} */
    this._observedSentinel = null;
    /** @type {ResizeObserver | null} */
    this._footerResizeObserver = null;
    /** @type {{dispose: Function, update: Function, _config: {title: string}} | null} */
    this._nextTooltipInstance = null;
    /** @type {string | null} */
    this._returnTo = null;
    /** @type {CharacterFormData} */
    this._formData = this._getInitialFormData();
  }

  /**
   * @returns {CharacterFormData}
   */
  _getInitialFormData() {
    return {
      name: '',
      classSlug: '',
      backgroundSlug: '',
      speciesSlug: '',
      alignment: '',
      abilityScores: { str: 0, dex: 0, con: 0, int: 0, wis: 0, cha: 0 },
      abilityScoreAdjustmentMethod: '',
      abilityScoreAdjustmentPrimary: '',
      abilityScoreAdjustmentSecondary: '',
      /** @type {Array<string>} */
      skillProficiencies: [],
      /** @type {{[key: string]: Array<string>}} */
      featureChoices: {},
      /** @type {Array<string>} */
      languages: [],
      equipmentPackageId: '',
      tokenId: '',
      backgroundEquipmentPackageId: '',
      backgroundToolChoice: '',
      /** @type {{[key: string]: string}} */
      featChoices: {},
      /** @type {Array<string>} */
      replacementSkills: [],
      speciesSize: '',
      speciesLineage: '',
      /** @type {{[key: string]: string}} */
      speciesTraitChoices: {},
      /** @type {{[key: string]: string}} */
      originFeatChoices: {},
      equipmentChanges: {
        /** @type {Array<{slug: string, quantity: number}>} */
        soldItems: [],
        /** @type {Array<{slug: string, quantity: number}>} */
        purchasedItems: [],
      },
    };
  }

  async connectedCallback() {
    await super.connectedCallback();
    this._loadSavedState();
    this._loadInitialData();

    // Set up scroll listener for sticky header
    await this.updateComplete;
    this._setupHeaderObserver();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._scrollContainer && this._scrollHandler) {
      this._scrollContainer.removeEventListener('scroll', this._scrollHandler);
      this._scrollContainer = null;
      this._scrollHandler = null;
    }
    if (this._footerResizeObserver) {
      this._footerResizeObserver.disconnect();
      this._footerResizeObserver = null;
    }
    if (this._nextTooltipInstance) {
      this._nextTooltipInstance.dispose();
      this._nextTooltipInstance = null;
    }
    this._disposeDiceTooltips();
  }

  _setupHeaderObserver() {
    const sentinel = this.renderRoot?.querySelector('.header-sentinel');
    if (!sentinel) return;

    // Only re-setup if sentinel element changed (step change)
    if (this._observedSentinel === sentinel) return;

    // Remove any previous scroll listener
    if (this._scrollContainer && this._scrollHandler) {
      this._scrollContainer.removeEventListener('scroll', this._scrollHandler);
    }
    this._isHeaderStuck = false;
    this._observedSentinel = sentinel;

    // Find the scroll container (modal-body in app-viewport's shadow DOM, or <main> in rpgish-app)
    const modalView = this.closest('app-viewport');
    const scrollContainer =
      modalView?.shadowRoot?.querySelector('.modal-body') ||
      document.querySelector('rpgish-app')?.shadowRoot?.querySelector('main');
    if (!scrollContainer) return;

    this._scrollContainer = /** @type {HTMLElement} */ (scrollContainer);
    this._scrollHandler = () => {
      const sentinelRect = sentinel.getBoundingClientRect();
      const containerRect = scrollContainer.getBoundingClientRect();
      const stuck = sentinelRect.top < containerRect.top;
      if (this._isHeaderStuck !== stuck) {
        this._isHeaderStuck = stuck;
      }
    };
    scrollContainer.addEventListener('scroll', this._scrollHandler, { passive: true });
  }

  // TODO: Footer/sidebar measurements reach into rpgish-app's shadow DOM — consider CSS custom properties set by the app shell instead
  _measureSiteFooter() {
    const appFooter = document.querySelector('rpgish-app')?.shadowRoot?.querySelector('app-footer');
    if (appFooter && !this._footerResizeObserver) {
      const measure = () => {
        const height = appFooter.getBoundingClientRect().height;
        this.style.setProperty('--site-footer-height', `${height}px`);
      };
      measure();
      this._footerResizeObserver = new ResizeObserver(measure);
      this._footerResizeObserver.observe(appFooter);
    }
    // Also measure sidebar width
    const sidebar = document.querySelector('rpgish-app')?.shadowRoot?.querySelector('nav');
    if (sidebar) {
      const width = sidebar.getBoundingClientRect().width;
      this.style.setProperty('--sidebar-width', `${width}px`);
    }
  }

  updated(/** @type {Map<string, unknown>} */ changedProperties) {
    super.updated(changedProperties);
    if (!this._loading) {
      this._setupHeaderObserver();
      this._measureSiteFooter();
      this._updateNextButtonTooltip();
      this._updateDiceTooltips();
    }
  }

  // TODO: Consider a tooltip component or directive instead of manually managing Bootstrap Tooltip instances
  _updateNextButtonTooltip() {
    const tooltipText = this._nextButtonTooltip;
    const el = this.renderRoot?.querySelector('[data-next-tooltip]');

    if (!tooltipText || !el) {
      if (this._nextTooltipInstance) {
        this._nextTooltipInstance.dispose();
        this._nextTooltipInstance = null;
      }
      return;
    }

    // Inject one-time global style for multiline tooltip
    if (!document.querySelector('#next-btn-tooltip-style')) {
      const style = document.createElement('style');
      style.id = 'next-btn-tooltip-style';
      style.textContent = '.next-btn-tooltip .tooltip-inner { white-space: pre-line; text-align: left; }';
      document.head.appendChild(style);
    }

    const Tooltip =
      /** @type {{bootstrap?: {Tooltip: {new(el: Element, opts?: object): {dispose(): void, update(): void, _config: {title: string}}}}}} */ (
        window
      ).bootstrap?.Tooltip;
    if (!Tooltip) return;

    if (this._nextTooltipInstance) {
      this._nextTooltipInstance._config.title = tooltipText;
      this._nextTooltipInstance.update();
    } else {
      this._nextTooltipInstance = new Tooltip(el, {
        title: tooltipText,
        placement: 'top',
        container: 'body',
        customClass: 'next-btn-tooltip',
      });
    }
  }

  _updateDiceTooltips() {
    this._disposeDiceTooltips();
    const Tooltip = /** @type {{bootstrap?: {Tooltip: {new(el: Element, opts?: object): {dispose(): void}}}}} */ (
      window
    ).bootstrap?.Tooltip;
    if (!Tooltip) return;
    /** @type {{dispose: Function}[] | undefined} */
    this._diceTooltipInstances = [];
    const els = this.renderRoot?.querySelectorAll('[data-bs-toggle="tooltip"]') || [];
    els.forEach((el) => {
      this._diceTooltipInstances?.push(new Tooltip(el, { container: 'body' }));
    });
  }

  _disposeDiceTooltips() {
    if (this._diceTooltipInstances) {
      this._diceTooltipInstances.forEach((t) => {
        t.dispose();
      });
      this._diceTooltipInstances = undefined;
    }
  }

  _loadSavedState() {
    // Check for skipRestore query param
    const query = router.getQuery();
    // Capture the optional returnTo so it survives the skipRestore cleanup
    // navigation below and is still available when the character is saved.
    const returnTo = query.get('returnTo');
    if (returnTo) {
      this._returnTo = returnTo;
    }
    if (query.get('skipRestore') === 'true') {
      this._clearSavedState();
      // Remove the query param by navigating clean, preserving returnTo so
      // _createCharacter can read it from the query when creation completes.
      const target = returnTo
        ? `/characters/new/custom?returnTo=${encodeURIComponent(returnTo)}`
        : '/characters/new/custom';
      router.navigate(target);
      return;
    }

    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const state = JSON.parse(saved);
        if (state.currentStep !== undefined) {
          this._currentStep = state.currentStep;
        }
        if (state.formData) {
          this._formData = { ...this._getInitialFormData(), ...state.formData };
        }
      }
    } catch (e) {
      console.warn('Failed to load character creation state', e);
    }
  }

  _saveState() {
    try {
      const state = {
        currentStep: this._currentStep,
        formData: this._formData,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      console.warn('Failed to save character creation state', e);
    }
  }

  _clearSavedState() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      console.warn('Failed to clear character creation state', e);
    }
  }

  async _loadInitialData() {
    this._loading = true;
    this._error = null;

    try {
      const [classes, backgrounds, species] = await Promise.all([
        api.getClasses(),
        api.getBackgrounds(),
        api.getSpecies(),
      ]);

      this._classes = /** @type {Array<ClassListItem>} */ (classes);
      this._backgrounds = /** @type {Array<BackgroundListItem>} */ (backgrounds);
      this._species = /** @type {Array<SpeciesListItem>} */ (species);

      // If we have saved state, reload the options
      if (this._formData.classSlug) {
        await this._loadClassOptions(this._formData.classSlug);
      }
      if (this._formData.backgroundSlug) {
        await this._loadBackgroundOptions(this._formData.backgroundSlug);
      }
      if (this._currentStep >= 4) {
        this._loadEquipment();
      }
      if (this._currentStep >= 6) {
        this._loadTokens();
      }
      if (this._formData.speciesSlug) {
        await this._loadSpeciesOptions(this._formData.speciesSlug);
        if (this._speciesNeedsOriginFeats()) {
          await this._loadOriginFeats();
          // If an origin feat was previously selected, reload its options
          const selectedFeatSlug = Object.values(this._formData.speciesTraitChoices || {}).find((v) =>
            this._originFeats.some((f) => f.slug === v),
          );
          if (selectedFeatSlug) {
            const spellList = this._formData.originFeatChoices?.spellList;
            await this._loadOriginFeatOptions(selectedFeatSlug, spellList ? { spellList } : undefined);
          }
        }
      }
    } catch (err) {
      console.error('Failed to load initial data:', err);
      this._error = 'Failed to load character creation data. Please try again.';
    } finally {
      this._loading = false;
    }
  }

  /**
   * @param {string} classSlug
   */
  async _loadClassOptions(classSlug) {
    try {
      const options = /** @type {ClassCreationOptions} */ (await api.getClassCreationChoices(classSlug));
      this._classOptions = options;
      this._pruneStaleFeatureChoices();
    } catch (err) {
      console.error('Failed to load class options:', err);
    }
  }

  /**
   * Drops any entries in _formData.featureChoices whose slugs aren't
   * valid options for the now-loaded class choices. Defends against
   * stale localStorage state when the schema shifts (e.g. a new
   * choice inserted ahead of an existing one renumbers the indices
   * that the state is keyed by). Only prunes choices whose options
   * have already been resolved to an object array — placeholder
   * choices (string `{choices.foo}` references) are left alone so a
   * later render pass can resolve them.
   */
  _pruneStaleFeatureChoices() {
    const features = this._classOptions?.features ?? [];
    /** @type {{[key: string]: Set<string>}} */
    const validSlugsByKey = {};
    features.forEach((feature) => {
      (feature.choices ?? []).forEach((/** @type {FeatureChoice} */ choice, idx) => {
        if (!Array.isArray(choice.options)) return;
        const slugs = choice.options
          .map((/** @type {ChoiceOption | string} */ o) => (typeof o === 'string' ? o : o?.slug))
          .filter((/** @type {string|undefined} */ s) => typeof s === 'string' && s.length > 0);
        if (slugs.length === 0) return;
        validSlugsByKey[`${feature.slug}:${idx}`] = new Set(/** @type {Array<string>} */ (slugs));
      });
    });

    const featureChoices = { ...this._formData.featureChoices };
    let changed = false;
    for (const [key, sels] of Object.entries(featureChoices)) {
      const validSet = validSlugsByKey[key];
      if (!validSet) continue;
      const filtered = sels.filter((/** @type {string} */ s) => validSet.has(s));
      if (filtered.length !== sels.length) {
        featureChoices[key] = filtered;
        changed = true;
      }
    }
    if (changed) {
      this._formData = { ...this._formData, featureChoices };
      this._saveState();
    }
  }

  /**
   * @param {string} backgroundSlug
   */
  async _loadBackgroundOptions(backgroundSlug) {
    try {
      const options = /** @type {BackgroundCreationOptions} */ (await api.getBackgroundCreationChoices(backgroundSlug));
      this._backgroundOptions = options;
      // Load feat options if background has a feat
      if (this._backgroundOptions?.feat?.slug) {
        // Apply prefilled choices from the background
        const prefilled = this._backgroundOptions.feat.prefilledChoices;
        if (prefilled) {
          const featChoices = { ...this._formData.featChoices };
          for (const [key, value] of Object.entries(prefilled)) {
            featChoices[key] = value;
          }
          this._formData = { ...this._formData, featChoices };
        }

        // If background prefills Magic Initiate spell list that conflicts with origin feat, clear origin
        if (this._backgroundOptions.feat.slug === 'magic-initiate') {
          const bgSpellList = this._formData.featChoices?.spellList;
          const originSpellList = this._formData.originFeatChoices?.spellList;
          if (bgSpellList && originSpellList && bgSpellList === originSpellList) {
            const originFeatChoices = { ...this._formData.originFeatChoices };
            delete originFeatChoices.spellList;
            delete originFeatChoices.cantrips;
            delete originFeatChoices.spell;
            this._formData = { ...this._formData, originFeatChoices };
            this._saveState();
          }
        }

        const spellList = this._formData.featChoices?.spellList;
        const featParams = spellList ? { spellList } : undefined;
        this._featOptions = /** @type {FeatCreationChoices} */ (
          await api.getFeatCreationChoices(this._backgroundOptions.feat.slug, featParams)
        );

        // Clear feat spell choices that conflict with other steps
        const classSpells = this._getAlreadyChosen('spell', 'feat');
        if (classSpells.size > 0 && this._featOptions?.choices) {
          const featChoices = { ...this._formData.featChoices };
          let changed = false;
          for (const choice of this._featOptions.choices) {
            if (!choice.id || choice.choiceType !== 'spell') continue;
            const val = featChoices[choice.id];
            if (!val) continue;
            if (getSelectionType(choice) === 'single' && classSpells.has(val)) {
              delete featChoices[choice.id];
              changed = true;
            } else if (getSelectionType(choice) === 'multi') {
              const filtered = val.split(',').filter((s) => s && !classSpells.has(s));
              if (filtered.length !== val.split(',').filter(Boolean).length) {
                featChoices[choice.id] = filtered.join(',');
                changed = true;
              }
            }
          }
          if (changed) {
            this._formData = { ...this._formData, featChoices };
            this._saveState();
          }
        }
      }
    } catch (err) {
      console.error('Failed to load background options:', err);
    }
  }

  /**
   * @param {string} speciesSlug
   */
  async _loadSpeciesOptions(speciesSlug) {
    try {
      const options = /** @type {SpeciesCreationOptions} */ (await api.getSpeciesCreationChoices(speciesSlug));
      this._speciesOptions = options;
    } catch (err) {
      console.error('Failed to load species options:', err);
    }
  }

  async _loadTokens() {
    if (this._tokens.length > 0) return;
    this._tokensLoading = true;
    try {
      const data = /** @type {{ tokens: Array<{slug: string, image_url: string}> }} */ (
        await api.get('/api/tokens?token_type=pc&pageSize=100')
      );
      const cdnBase = getCDNBaseUrl();
      this._tokens = (data.tokens || []).map((t) => ({
        slug: t.slug,
        name: t.slug,
        image_url: `${cdnBase}/token/${t.slug}`,
      }));
    } catch (err) {
      console.error('Failed to load tokens:', err);
    } finally {
      this._tokensLoading = false;
    }
  }

  async _loadEquipment() {
    if (this._equipment.length > 0) return;
    this._equipmentLoading = true;
    try {
      const equipment = /** @type {Array<EquipmentItem>} */ (/** @type {unknown} */ (await api.getEquipment()));
      this._equipment = equipment.filter((/** @type {EquipmentItem} */ eq) => eq.cost && eq.cost !== '0 cp');
    } catch (err) {
      console.error('Failed to load equipment:', err);
    } finally {
      this._equipmentLoading = false;
    }
  }

  async _loadOriginFeats() {
    try {
      const data = /** @type {Array<OriginFeatItem>} */ (await api.getFeats());
      this._originFeats = (data || []).filter((/** @type {OriginFeatItem} */ f) => f.category === 'origin');
    } catch (err) {
      console.error('Failed to load origin feats:', err);
    }
  }

  /**
   * @param {string} featSlug
   * @param {Record<string, string>} [params]
   */
  async _loadOriginFeatOptions(featSlug, params) {
    try {
      this._originFeatOptions = /** @type {FeatCreationChoices} */ (await api.getFeatCreationChoices(featSlug, params));
    } catch (err) {
      console.error('Failed to load origin feat options:', err);
    }
  }

  /**
   * @param {string} featSlug
   * @param {Record<string, string>} [params]
   */
  async _loadFeatOptions(featSlug, params) {
    try {
      this._featOptions = /** @type {FeatCreationChoices} */ (await api.getFeatCreationChoices(featSlug, params));
    } catch (err) {
      console.error('Failed to load feat options:', err);
    }
  }

  // Computed getters for skill overlap
  get _duplicateSkills() {
    if (!this._backgroundOptions?.skills?.skills) return [];
    const backgroundSkills = this._backgroundOptions.skills.skills;
    const classSkills = this._formData.skillProficiencies;
    return classSkills.filter((skill) => backgroundSkills.includes(skill));
  }

  get _needsSkillReplacement() {
    return this._duplicateSkills.length > 0;
  }

  get _availableReplacementSkills() {
    const backgroundSkills = new Set(this._backgroundOptions?.skills?.skills || []);
    return ALL_SKILLS.filter((s) => !backgroundSkills.has(s) || this._formData.replacementSkills.includes(s));
  }

  /** Returns the spell list chosen for background Magic Initiate, or null */
  get _backgroundMagicInitiateSpellList() {
    if (this._backgroundOptions?.feat?.slug !== 'magic-initiate') return null;
    return this._formData.featChoices?.spellList || null;
  }

  /** Returns recommended feat choice slugs for the current class */
  get _featRecommendedSlugs() {
    const classSlug = this._formData.classSlug || 'default';
    return this._featOptions?.recommendedByClass?.[classSlug] || this._featOptions?.recommendedByClass?.default || {};
  }

  /** Returns the spell list chosen for origin feat Magic Initiate, or null */
  get _originMagicInitiateSpellList() {
    const isOriginMI = Object.values(this._formData.speciesTraitChoices || {}).includes('magic-initiate');
    if (!isOriginMI) return null;
    return this._formData.originFeatChoices?.spellList || null;
  }

  /**
   * Returns a Set of already-chosen values for a given choice type across all steps,
   * excluding the specified source to avoid self-conflicts.
   * @param {string} choiceType - e.g. 'spell', 'skill'
   * @param {'class'|'feat'|'originFeat'} [excludeSource]
   * @returns {Set<string>}
   */
  _getAlreadyChosen(choiceType, excludeSource) {
    const chosen = new Set();

    // Base skill proficiencies (class + background + replacements)
    if (choiceType === 'skill') {
      for (const s of this._formData.skillProficiencies) chosen.add(s);
      for (const s of this._backgroundOptions?.skills?.skills || []) chosen.add(s);
      for (const s of this._formData.replacementSkills) chosen.add(s);
    }

    // Class feature choices
    if (excludeSource !== 'class') {
      for (const feature of this._classOptions?.features || []) {
        if (!feature.choices) continue;
        for (let i = 0; i < feature.choices.length; i++) {
          if (/** @type {FeatureChoice} */ (feature.choices[i]).choiceType !== choiceType) continue;
          const selections = this._formData.featureChoices[`${feature.slug}:${i}`] || [];
          for (const slug of selections) chosen.add(slug);
        }
      }
    }

    // Background feat choices
    if (excludeSource !== 'feat') {
      for (const choice of this._featOptions?.choices || []) {
        if (!choice.id || choice.choiceType !== choiceType) continue;
        const val = this._formData.featChoices[choice.id];
        if (!val) continue;
        for (const slug of val.split(',').filter(Boolean)) chosen.add(slug);
      }
    }

    // Origin feat choices
    if (excludeSource !== 'originFeat') {
      for (const choice of this._originFeatOptions?.choices || []) {
        if (!choice.id || choice.choiceType !== choiceType) continue;
        const val = this._formData.originFeatChoices[choice.id];
        if (!val) continue;
        for (const slug of val.split(',').filter(Boolean)) chosen.add(slug);
      }
    }

    return chosen;
  }

  get _currentGoldCopper() {
    const startingGold = computeStartingGoldCopper(
      this._classOptions?.equipmentPackages,
      this._formData.equipmentPackageId,
      this._backgroundOptions?.equipmentPackages,
      this._formData.backgroundEquipmentPackageId,
    );
    return computeCurrentGoldCopper(startingGold, this._formData.equipmentChanges, this._equipment || []);
  }

  get _currentInventory() {
    return computeCurrentInventory(
      this._classOptions?.equipmentPackages,
      this._formData.equipmentPackageId,
      this._backgroundOptions?.equipmentPackages,
      this._formData.backgroundEquipmentPackageId,
      this._formData.backgroundToolChoice,
      this._formData.equipmentChanges,
      this._equipment,
    );
  }

  // Selection handlers
  /**
   * @param {string} classSlug
   */
  async _selectClass(classSlug) {
    if (this._formData.classSlug === classSlug) return;

    this._classOptions = null;
    this._formData = {
      ...this._formData,
      classSlug,
      skillProficiencies: [],
      featureChoices: {},
      equipmentPackageId: '',
    };

    await this._loadClassOptions(classSlug);
    this._saveState();
  }

  /**
   * @param {string} backgroundSlug
   */
  async _selectBackground(backgroundSlug) {
    if (this._formData.backgroundSlug === backgroundSlug) return;

    this._formData = {
      ...this._formData,
      backgroundSlug,
      backgroundEquipmentPackageId: '',
      backgroundToolChoice: '',
      featChoices: {},
      replacementSkills: [],
      abilityScoreAdjustmentMethod: '',
      abilityScoreAdjustmentPrimary: '',
      abilityScoreAdjustmentSecondary: '',
    };

    await this._loadBackgroundOptions(backgroundSlug);
    this._saveState();
  }

  /**
   * @param {string} speciesSlug
   */
  async _selectSpecies(speciesSlug) {
    if (this._formData.speciesSlug === speciesSlug) return;

    this._formData = {
      ...this._formData,
      speciesSlug,
      speciesSize: '',
      speciesLineage: '',
      speciesTraitChoices: {},
      originFeatChoices: {},
      languages: [],
    };

    this._speciesOptions = null;
    this._originFeatOptions = null;
    await this._loadSpeciesOptions(speciesSlug);

    // Check if species needs origin feats (traits with {rules/feats/origin} reference)
    if (this._speciesNeedsOriginFeats()) {
      await this._loadOriginFeats();
    }

    this._saveState();
  }

  /**
   * Check if current species has traits that reference origin feats
   * @returns {boolean}
   */
  _speciesNeedsOriginFeats() {
    if (!this._speciesOptions?.specialTraits) return false;
    for (const trait of this._speciesOptions.specialTraits) {
      if (trait.choices) {
        for (const choice of trait.choices) {
          if (isOriginFeatChoice(choice)) return true;
        }
      }
    }
    return false;
  }

  /**
   * @param {string} lineageSlug
   */
  async _selectLineage(lineageSlug) {
    this._formData = {
      ...this._formData,
      speciesLineage: lineageSlug,
      speciesTraitChoices: {},
    };
    this._saveState();
  }

  /**
   * @param {string} traitKey
   * @param {string} value
   */
  async _selectTraitChoice(traitKey, value) {
    const speciesTraitChoices = { ...this._formData.speciesTraitChoices, [traitKey]: value };
    this._formData = { ...this._formData, speciesTraitChoices };
    this._saveState();

    // If choosing an origin feat, load its options
    if (value && this._originFeats.some((f) => f.slug === value)) {
      await this._loadOriginFeatOptions(value);
    }
  }

  /**
   * @param {string} choiceId
   * @param {string} value
   */
  async _selectOriginFeatChoice(choiceId, value) {
    const originFeatChoices = { ...this._formData.originFeatChoices, [choiceId]: value };

    // If changing the spell list, clear spell-dependent choices (cantrips, spell)
    if (choiceId === 'spellList') {
      delete originFeatChoices.cantrips;
      delete originFeatChoices.spell;
    }

    this._formData = { ...this._formData, originFeatChoices };
    this._saveState();

    // If this is the spellList choice, reload origin feat options with the selected list
    if (choiceId === 'spellList') {
      const selectedFeatSlug = Object.values(this._formData.speciesTraitChoices).find((v) =>
        this._originFeats.some((f) => f.slug === v),
      );
      if (selectedFeatSlug) {
        await this._loadOriginFeatOptions(selectedFeatSlug, { spellList: value });
      }
    }
  }

  /**
   * @param {string} spellSlug
   */
  /**
   * Toggle a multi-select spell for feat or origin feat choices
   * @param {'featChoices' | 'originFeatChoices'} formDataKey
   * @param {FeatCreationChoices | null | undefined} optionsSource
   * @param {string} choiceId
   * @param {string} spellSlug
   */
  _toggleMultiSpell(formDataKey, optionsSource, choiceId, spellSlug) {
    const choices = { ...this._formData[formDataKey] };
    const current = choices[choiceId] ? choices[choiceId].split(',').filter(Boolean) : [];
    const index = current.indexOf(spellSlug);
    const maxSpells = optionsSource?.choices?.find((c) => c.id === choiceId)?.count || 1;

    if (index >= 0) {
      current.splice(index, 1);
    } else if (current.length < maxSpells) {
      current.push(spellSlug);
    }

    choices[choiceId] = current.join(',');
    this._formData = { ...this._formData, [formDataKey]: choices };
    this._saveState();
  }

  /**
   * Get the required count for a choice, handling countModifiedBy
   * @param {FeatureChoice} choice
   * @returns {number}
   */
  _getChoiceRequiredCount(choice) {
    if (getSelectionType(choice) === 'single') return 1;
    if (choice.countModifiedBy) {
      const { feature: modFeature, option: modOption, count: modCount } = choice.countModifiedBy;
      for (let i = 0; i < 10; i++) {
        const featureSelections = this._formData.featureChoices[`${modFeature}:${i}`] || [];
        if (featureSelections.includes(modOption)) return modCount;
      }
    }
    return choice.count || 1;
  }

  /**
   * @param {string} skillSlug
   */
  _toggleSkill(skillSlug) {
    /** @type {Array<string>} */
    const skills = [...this._formData.skillProficiencies];
    const index = skills.indexOf(skillSlug);
    const maxSkills = this._classOptions?.skillProficiencies?.count || 0;

    if (index >= 0) {
      skills.splice(index, 1);
      // Also remove from any expertise selections
      /** @type {{[key: string]: Array<string>}} */
      const featureChoices = { ...this._formData.featureChoices };
      for (const key in featureChoices) {
        const selections = featureChoices[key];
        if (selections?.includes(skillSlug)) {
          featureChoices[key] = selections.filter((s) => s !== skillSlug);
        }
      }
      this._formData = { ...this._formData, skillProficiencies: skills, featureChoices };
    } else if (skills.length < maxSkills) {
      skills.push(skillSlug);
      this._formData = { ...this._formData, skillProficiencies: skills };
    } else {
      return;
    }

    this._saveState();
  }

  /**
   * @param {string} featureSlug
   * @param {number} choiceIndex
   * @param {string} optionSlug
   */
  _selectChoiceOption(featureSlug, choiceIndex, optionSlug) {
    const key = `${featureSlug}:${choiceIndex}`;
    /** @type {{[key: string]: Array<string>}} */
    const featureChoices = { ...this._formData.featureChoices };
    featureChoices[key] = [optionSlug];
    this._formData = { ...this._formData, featureChoices };
    this._saveState();
  }

  /**
   * @param {string} featureSlug
   * @param {number} choiceIndex
   * @param {string} optionSlug
   * @param {number} maxCount
   */
  _toggleChoiceOption(featureSlug, choiceIndex, optionSlug, maxCount) {
    const key = `${featureSlug}:${choiceIndex}`;
    /** @type {{[key: string]: Array<string>}} */
    const featureChoices = { ...this._formData.featureChoices };
    const current = featureChoices[key] || [];
    const index = current.indexOf(optionSlug);

    if (index >= 0) {
      featureChoices[key] = current.filter((/** @type {string} */ s) => s !== optionSlug);
    } else if (current.length < maxCount) {
      featureChoices[key] = [...current, optionSlug];
    }

    this._formData = { ...this._formData, featureChoices };
    this._saveState();
  }

  /**
   * @param {string} langSlug
   */
  _toggleLanguage(langSlug) {
    /** @type {Array<string>} */
    const langs = [...this._formData.languages];
    const index = langs.indexOf(langSlug);

    if (index >= 0) {
      langs.splice(index, 1);
    } else if (langs.length < 2) {
      langs.push(langSlug);
    }

    this._formData = { ...this._formData, languages: langs };
    this._saveState();
  }

  /**
   * @param {string} choiceId
   * @param {string} value
   */
  async _selectFeatChoice(choiceId, value) {
    const featChoices = { ...this._formData.featChoices, [choiceId]: value };
    this._formData = { ...this._formData, featChoices };
    this._saveState();

    // If this is a spellList choice, reload feat options with the new spell list
    const choice = this._featOptions?.choices?.find((c) => c.id === choiceId);
    if (choice && isSpellListChoice(choice) && this._backgroundOptions?.feat?.slug) {
      await this._loadFeatOptions(this._backgroundOptions.feat.slug, { spellList: value });
    }
  }

  /**
   * @param {string} skillSlug
   */
  _toggleReplacementSkill(skillSlug) {
    const replacementSkills = [...this._formData.replacementSkills];
    const index = replacementSkills.indexOf(skillSlug);
    const needed = this._duplicateSkills.length;

    if (index >= 0) {
      replacementSkills.splice(index, 1);
    } else if (replacementSkills.length < needed) {
      replacementSkills.push(skillSlug);
    }

    this._formData = { ...this._formData, replacementSkills };
    this._saveState();
  }

  // TODO: Move buy/sell logic into equipment-step once it owns _equipment data.
  // These handlers only need _formData.equipmentChanges (which could be emitted
  // as a single updated object) rather than fine-grained sell/buy events.
  /**
   * @param {string} slug
   */
  _sellOneItem(slug) {
    const eq = this._equipment.find((e) => e.slug === slug);
    const qty = eq?.bundlePricing && eq?.bundleSize ? eq.bundleSize : 1;

    const equipmentChanges = {
      soldItems: [...this._formData.equipmentChanges.soldItems],
      purchasedItems: [...this._formData.equipmentChanges.purchasedItems],
    };

    // Check if we can undo a purchase first
    const purchasedIndex = equipmentChanges.purchasedItems.findIndex((i) => i.slug === slug);
    if (purchasedIndex >= 0) {
      const item = /** @type {NonNullable<typeof equipmentChanges.purchasedItems[0]>} */ (
        equipmentChanges.purchasedItems[purchasedIndex]
      );
      if (item.quantity > qty) {
        equipmentChanges.purchasedItems[purchasedIndex] = { ...item, quantity: item.quantity - qty };
      } else {
        equipmentChanges.purchasedItems.splice(purchasedIndex, 1);
      }
    } else {
      // Sell from package inventory
      const invItem = this._currentInventory.find((e) => e.slug === slug);
      if (!invItem) return;
      const existingSold = equipmentChanges.soldItems.find((i) => i.slug === slug);
      if (existingSold) {
        equipmentChanges.soldItems = equipmentChanges.soldItems.map((i) =>
          i.slug === slug ? { ...i, quantity: (i.quantity || qty) + qty } : i,
        );
      } else {
        equipmentChanges.soldItems.push({ slug, name: invItem.name, cost: invItem.cost, quantity: qty });
      }
    }

    this._formData = { ...this._formData, equipmentChanges };
    this._saveState();
  }

  /**
   * @param {string} slug
   */
  _buyOneItem(slug) {
    const eq = this._equipment.find((e) => e.slug === slug);
    if (!eq) return;

    const cost = parseCostToCopper(eq.cost || '0 cp');
    if (cost > this._currentGoldCopper) return;

    const qty = eq.bundlePricing && eq.bundleSize ? eq.bundleSize : 1;

    const equipmentChanges = {
      soldItems: [...this._formData.equipmentChanges.soldItems],
      purchasedItems: [...this._formData.equipmentChanges.purchasedItems],
    };

    // Check if we can undo a sale first
    const soldIndex = equipmentChanges.soldItems.findIndex((i) => i.slug === slug);
    if (soldIndex >= 0) {
      const item = /** @type {NonNullable<typeof equipmentChanges.soldItems[0]>} */ (
        equipmentChanges.soldItems[soldIndex]
      );
      if (item.quantity > qty) {
        equipmentChanges.soldItems[soldIndex] = { ...item, quantity: item.quantity - qty };
      } else {
        equipmentChanges.soldItems.splice(soldIndex, 1);
      }
    } else {
      const existingPurchased = equipmentChanges.purchasedItems.find((i) => i.slug === slug);
      if (existingPurchased) {
        equipmentChanges.purchasedItems = equipmentChanges.purchasedItems.map((i) =>
          i.slug === slug ? { ...i, quantity: (i.quantity || qty) + qty } : i,
        );
      } else {
        equipmentChanges.purchasedItems.push({ slug, name: eq.name, cost: eq.cost, quantity: qty });
      }
    }

    this._formData = { ...this._formData, equipmentChanges };
    this._saveState();
  }

  _resetEquipmentChanges() {
    this._formData = {
      ...this._formData,
      equipmentChanges: { soldItems: [], purchasedItems: [] },
    };
    this._saveState();
  }

  _randomizeName() {
    const name = FANTASY_FIRST_NAMES[Math.floor(Math.random() * FANTASY_FIRST_NAMES.length)] || 'Adventurer';
    this._formData = { ...this._formData, name };
    this._saveState();
  }

  _randomizeToken() {
    if (this._tokens.length === 0) return;
    const token = this._tokens[Math.floor(Math.random() * this._tokens.length)];
    if (token) {
      this._formData = { ...this._formData, tokenId: token.slug };
      this._saveState();
    }
  }

  /** @param {CustomEvent} e */
  _onAbilityAssignerChange(e) {
    this._formData = {
      ...this._formData,
      abilityScores: e.detail.abilityScores,
      abilityScoreAdjustmentMethod: e.detail.adjustmentMethod,
      abilityScoreAdjustmentPrimary: e.detail.adjustmentPrimary,
      abilityScoreAdjustmentSecondary: e.detail.adjustmentSecondary,
    };
    this._saveState();
  }

  // Computed getters
  get _assignedCount() {
    return Object.values(this._formData.abilityScores).filter((v) => v > 0).length;
  }

  get _backgroundAbilities() {
    return this._backgroundOptions?.abilityScores?.abilities || [];
  }

  /**
   * Validate the current step, returning missing items.
   * If empty, the step is complete and the user can proceed.
   *
   * TODO: Consider moving validation into each step component via a shared interface
   * (e.g. a getValidationMissing() method). The parent could query the active step
   * component instead of reimplementing each step's validation logic here. This would
   * let _classOptions, _backgroundOptions, _speciesOptions, and _featOptions move into
   * their respective step components too, since the parent mainly holds them for validation.
   *
   * @returns {Array<string>}
   */
  _getValidationMissing() {
    /** @type {Array<string>} */
    const missing = [];
    switch (this._currentStep) {
      case 0: {
        // Class
        if (!this._formData.classSlug) {
          missing.push('Select a class');
          break;
        }
        if (!this._classOptions) break;
        if (this._classOptions.skillProficiencies) {
          const remaining = this._classOptions.skillProficiencies.count - this._formData.skillProficiencies.length;
          if (remaining > 0) missing.push(`Select ${remaining} more skill proficienc${remaining === 1 ? 'y' : 'ies'}`);
        }
        if ((this._classOptions.equipmentPackages?.length ?? 0) > 0 && !this._formData.equipmentPackageId) {
          missing.push('Choose a starting equipment package');
        }
        for (const feature of this._classOptions.features || []) {
          if (!feature.choices) continue;
          for (let i = 0; i < feature.choices.length; i++) {
            const choice = feature.choices[i];
            if (!choice) continue;
            const key = `${feature.slug}:${i}`;
            const selections = this._formData.featureChoices[key] || [];
            const required = this._getChoiceRequiredCount(choice);
            const remaining = required - selections.length;
            if (remaining > 0) missing.push(`Choose ${remaining} more for ${getChoiceLabel(choice)}`);
          }
        }
        break;
      }
      case 1: {
        // Background
        if (!this._formData.backgroundSlug) {
          missing.push('Select a background');
          break;
        }
        if (!this._backgroundOptions) break;
        if (
          (this._backgroundOptions.equipmentPackages?.length ?? 0) > 0 &&
          !this._formData.backgroundEquipmentPackageId
        ) {
          missing.push('Choose a starting equipment package');
        }
        if (this._backgroundOptions.tools?.type === 'choice' && !this._formData.backgroundToolChoice) {
          missing.push('Choose a tool proficiency');
        }
        if (this._featOptions?.choices) {
          const classSpells = this._getAlreadyChosen('spell', 'feat');
          for (const choice of this._featOptions.choices) {
            if (!choice.id) continue;
            if (this._backgroundOptions?.feat?.prefilledChoices?.[choice.id]) continue;
            const selType = getSelectionType(choice);
            if (selType === 'single') {
              const val = this._formData.featChoices[choice.id];
              if (!val) {
                missing.push(`Choose ${getChoiceLabel(choice)}`);
              } else if (choice.choiceType === 'spell' && classSpells.has(val)) {
                missing.push(`${getChoiceLabel(choice)} conflicts with a class spell`);
              }
            }
            if (selType === 'multi') {
              const selected = this._formData.featChoices[choice.id]?.split(',').filter(Boolean) || [];
              const remaining = (choice.count || 1) - selected.length;
              if (remaining > 0) missing.push(`Choose ${remaining} more for ${getChoiceLabel(choice)}`);
              if (choice.choiceType === 'spell' && selected.some((s) => classSpells.has(s))) {
                missing.push(`A ${getChoiceLabel(choice)} selection conflicts with a class spell`);
              }
            }
          }
        }
        if (this._needsSkillReplacement) {
          const remaining = this._duplicateSkills.length - this._formData.replacementSkills.length;
          if (remaining > 0) missing.push(`Choose ${remaining} replacement skill${remaining === 1 ? '' : 's'}`);
        }
        break;
      }
      case 2: {
        // Species
        if (!this._formData.speciesSlug) {
          missing.push('Select a species');
          break;
        }
        if (!this._speciesOptions) break;
        if (this._speciesOptions.species.size.length > 1 && !this._formData.speciesSize) {
          missing.push('Choose a size');
        }
        if (this._speciesOptions.lineages && Object.keys(this._speciesOptions.lineages).length > 0) {
          if (!this._formData.speciesLineage) {
            missing.push('Choose a lineage');
          } else {
            const lineageData = this._speciesOptions.lineages[this._formData.speciesLineage];
            if (lineageData?.choices) {
              for (const choice of lineageData.choices) {
                if (!choice.id) continue;
                if (!this._formData.speciesTraitChoices[`lineage.${choice.id}`]) {
                  missing.push(`Choose ${getChoiceLabel(choice) || 'a lineage option'}`);
                }
              }
            }
          }
        }
        if (this._speciesOptions.specialTraits) {
          for (const trait of this._speciesOptions.specialTraits) {
            if (!trait.choices) continue;
            for (const choice of trait.choices) {
              if (!choice.id) continue;
              if (isOriginFeatChoice(choice)) {
                if (!this._formData.speciesTraitChoices[choice.id]) {
                  missing.push('Choose an origin feat');
                } else if (this._originFeatOptions?.choices) {
                  const alreadyChosen = this._getAlreadyChosen('spell', 'originFeat');
                  for (const fChoice of this._originFeatOptions.choices) {
                    if (!fChoice.id) continue;
                    const selType = getSelectionType(fChoice);
                    if (selType === 'single') {
                      const val = this._formData.originFeatChoices[fChoice.id];
                      if (!val) {
                        missing.push(`Choose ${getChoiceLabel(fChoice) || 'an origin feat option'}`);
                      } else if (fChoice.choiceType === 'spell' && alreadyChosen.has(val)) {
                        missing.push(`${getChoiceLabel(fChoice)} conflicts with another spell`);
                      }
                    }
                    if (selType === 'multi') {
                      const selected = this._formData.originFeatChoices[fChoice.id]?.split(',').filter(Boolean) || [];
                      const remaining = (fChoice.count || 1) - selected.length;
                      if (remaining > 0) missing.push(`Choose ${remaining} more for ${getChoiceLabel(fChoice)}`);
                      if (fChoice.choiceType === 'spell' && selected.some((s) => alreadyChosen.has(s))) {
                        missing.push(`A ${getChoiceLabel(fChoice)} selection conflicts with another spell`);
                      }
                    }
                  }
                }
              } else {
                const classSkills = this._formData.skillProficiencies;
                const val = this._formData.speciesTraitChoices[choice.id];
                if (
                  !val &&
                  !(
                    choice.choiceType === 'skill' &&
                    Array.isArray(choice.options) &&
                    choice.options.every((/** @type {ChoiceOption | string} */ o) => {
                      const slug = typeof o === 'string' ? o : o.slug;
                      return classSkills.includes(slug);
                    })
                  )
                ) {
                  missing.push(`Choose ${getChoiceLabel(choice) || 'a trait option'}`);
                }
              }
            }
          }
        }
        const langsNeeded = 2 - this._formData.languages.length;
        if (langsNeeded > 0) missing.push(`Choose ${langsNeeded} more language${langsNeeded === 1 ? '' : 's'}`);
        break;
      }
      case 3: {
        // Abilities
        const unassigned = 6 - this._assignedCount;
        if (unassigned > 0) missing.push(`Assign ${unassigned} more ability score${unassigned === 1 ? '' : 's'}`);
        if (!this._formData.abilityScoreAdjustmentMethod) {
          missing.push('Choose a background adjustment method');
        } else if (this._formData.abilityScoreAdjustmentMethod === 'split') {
          if (!this._formData.abilityScoreAdjustmentPrimary) missing.push('Place the +2 bonus');
          if (!this._formData.abilityScoreAdjustmentSecondary) missing.push('Place the +1 bonus');
        }
        break;
      }
      case 4: // Equipment
        if (this._currentGoldCopper < 0) missing.push('Reduce purchases to stay within budget');
        break;
      case 5: // Alignment
        if (!this._formData.alignment) missing.push('Choose an alignment');
        break;
      case 6: {
        // Details
        if (this._formData.name.length < 2) missing.push('Enter a character name (at least 2 characters)');
        else if (this._formData.name.length > 30) missing.push('Character name must be 30 characters or fewer');
        if (!this._formData.tokenId) missing.push('Choose a portrait');
        break;
      }
    }
    return missing;
  }

  get _canProceed() {
    return this._getValidationMissing().length === 0;
  }

  get _nextButtonTooltip() {
    return this._getValidationMissing().join('\n');
  }

  // Navigation
  _nextStep() {
    if (this._currentStep < STEPS.length - 1 && this._canProceed) {
      this._currentStep++;
      this._saveState();

      // Load data for next step if needed
      if (this._currentStep === 4) {
        this._loadEquipment();
      } else if (this._currentStep === 6) {
        this._loadTokens();
      }
    }
  }

  _prevStep() {
    if (this._currentStep > 0) {
      this._currentStep--;
      this._saveState();
    }
  }

  _goToCharacters() {
    router.navigate('/characters');
  }

  async _createCharacter() {
    if (!this._canProceed) return;

    this._creating = true;
    this._createError = null;

    try {
      // Build ability score adjustment
      /** @type {{[key: string]: number}} */
      const abilityScoreAdjustment = {};
      if (this._formData.abilityScoreAdjustmentMethod === 'split') {
        if (this._formData.abilityScoreAdjustmentPrimary) {
          abilityScoreAdjustment[this._formData.abilityScoreAdjustmentPrimary] = 2;
        }
        if (this._formData.abilityScoreAdjustmentSecondary) {
          abilityScoreAdjustment[this._formData.abilityScoreAdjustmentSecondary] = 1;
        }
      } else if (this._formData.abilityScoreAdjustmentMethod === 'even') {
        for (const ability of this._backgroundAbilities) {
          abilityScoreAdjustment[ability] = 1;
        }
      }

      // Convert feature choices
      /** @type {{[key: string]: Array<string>}} */
      const featureChoicesFlat = {};
      /** @type {Array<string>} */
      const preparedSpells = [];

      for (const [key, selections] of Object.entries(this._formData.featureChoices)) {
        const parts = key.split(':');
        const featureSlug = parts[0];
        const choiceIndexStr = parts[1];
        if (!featureSlug || !choiceIndexStr) throw new Error(`Invalid feature choice key: ${key}`);
        const choiceIndex = parseInt(choiceIndexStr, 10);
        const feature = this._classOptions?.features?.find((/** @type {ClassFeature} */ f) => f.slug === featureSlug);
        const choice = feature?.choices?.[choiceIndex];
        const label = choice ? getChoiceLabel(choice) : '';

        // Skip selections from choices hidden by a visibleWhen
        // predicate. Otherwise stale picks (e.g. a Thaumaturge extra
        // cantrip from a previous selection state) would be submitted
        // even after the player switched back to Protector.
        if (choice?.visibleWhen) {
          const { feature: predFeature, option: predOption } = choice.visibleWhen;
          let visible = false;
          for (let i = 0; i < 10; i++) {
            const predSelections = this._formData.featureChoices[`${predFeature}:${i}`] || [];
            if (predSelections.includes(predOption)) {
              visible = true;
              break;
            }
          }
          if (!visible) continue;
        }

        if (label === 'Cantrips') {
          const cantrips = featureChoicesFlat.cantrips;
          if (!cantrips) {
            featureChoicesFlat.cantrips = [...selections];
          } else {
            cantrips.push(...selections);
          }
        } else if (label === 'Spellbook') {
          const spellbook = featureChoicesFlat.spellbook;
          if (!spellbook) {
            featureChoicesFlat.spellbook = [...selections];
          } else {
            spellbook.push(...selections);
          }
        } else if (label === 'Prepared Spells') {
          preparedSpells.push(...selections);
        } else if (choice?.id === 'thaumaturgeBonus') {
          featureChoicesFlat.thaumaturgeBonus = [...selections];
        } else {
          const existing = featureChoicesFlat[featureSlug];
          if (!existing) {
            featureChoicesFlat[featureSlug] = [...selections];
          } else {
            existing.push(...selections);
          }
        }
      }

      // Build species choices
      /** @type {{[key: string]: string}} */
      const traitChoices = {};
      /** @type {{[key: string]: string}} */
      const lineageChoices = {};
      for (const [key, value] of Object.entries(this._formData.speciesTraitChoices)) {
        if (key.startsWith('lineage.')) {
          lineageChoices[key.replace('lineage.', '')] = value;
        } else {
          const traitParts = key.split('.');
          const traitKey = traitParts[0];
          if (!traitKey) throw new Error(`Invalid trait choice key: ${key}`);
          traitChoices[traitKey] = value;
        }
      }

      const requestBody = {
        name: this._formData.name,
        class: this._formData.classSlug,
        species: this._formData.speciesSlug,
        background: this._formData.backgroundSlug,
        alignment: this._formData.alignment,
        token: this._formData.tokenId,
        abilityScores: this._formData.abilityScores,
        abilityScoreAdjustment,
        languages: ['common', ...this._formData.languages],
        classChoices: {
          skills: this._formData.skillProficiencies,
          equipmentPackage: this._formData.equipmentPackageId,
          featureChoices: featureChoicesFlat,
          preparedSpells: preparedSpells.length > 0 ? preparedSpells : undefined,
        },
        speciesChoices: {
          size: this._formData.speciesSize,
          lineage: this._formData.speciesLineage,
          traitChoices,
          lineageChoices,
          originFeatChoices: this._formData.originFeatChoices,
        },
        backgroundChoices: {
          featChoices: this._formData.featChoices,
          equipmentPackage: this._formData.backgroundEquipmentPackageId,
        },
        soldEquipment: this._formData.equipmentChanges.soldItems,
        purchasedEquipment: this._formData.equipmentChanges.purchasedItems,
      };

      const created = /** @type {{id: string}} */ (await api.createCharacter(requestBody));
      this._clearSavedState();
      // If the user arrived via a `returnTo` (e.g. from the Start Adventure
      // view), send them back there with the new character pre-selected.
      const returnTo = this._returnTo || router.getQuery().get('returnTo');
      if (returnTo && created?.id) {
        const separator = returnTo.includes('?') ? '&' : '?';
        router.navigate(`${returnTo}${separator}selectedCharacterId=${encodeURIComponent(created.id)}`);
      } else {
        router.navigate('/characters');
      }
    } catch (err) {
      console.error('Failed to create character:', err);
      this._createError = /** @type {Error} */ (err).message || 'Failed to create character';
    } finally {
      this._creating = false;
    }
  }

  // Rendering
  render() {
    return html`
      <div style="display: flex; flex-direction: column; flex: 1; min-width: 0;">
        <div style="padding: 1.25rem; padding-bottom: 0;">
          <!-- Header with step indicator (non-sticky) -->
          <div class="mb-4">
            <div class="d-flex align-items-center gap-2 mb-3 flex-wrap">
              ${STEPS.map(
                (step, index) => html`
                  <div
                    class="step-indicator ${index === this._currentStep ? 'active' : ''} ${
                      index < this._currentStep ? 'completed' : ''
                    }"
                  >
                    ${index < this._currentStep ? html`<span class="check-mark me-1">&#10003;</span>` : ''}
                    ${step.label}
                  </div>
                  ${
                    index < STEPS.length - 1
                      ? html` <div class="step-connector ${index < this._currentStep ? 'completed' : ''}"></div> `
                      : ''
                  }
                `,
              )}
            </div>
          </div>

          ${
            this._loading
              ? html`
                <div class="text-center py-5">
                  <div class="spinner-border" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                  <p class="mt-3 text-muted">Loading...</p>
                </div>
              `
              : this._error
                ? html`
                  <div class="alert alert-danger">
                    ${this._error}
                    <button class="btn btn-link p-0 ms-2" @click="${this._loadInitialData}">Retry</button>
                  </div>
                `
                : html` ${this._renderCurrentStep()} `
          }
        </div>

        <!-- Navigation footer -->
        ${
          !this._loading && !this._error
            ? html`
              <div class="character-creation-footer d-flex justify-content-between">
                ${
                  this._currentStep > 0
                    ? html`
                      <button class="btn btn-outline-secondary" @click="${this._prevStep}">
                        Back: ${STEPS[this._currentStep - 1]?.label}
                      </button>
                    `
                    : html`<div></div>`
                }

                <div class="d-flex gap-2">
                  <button class="btn btn-outline-secondary" @click="${this._goToCharacters}">Cancel</button>
                  ${
                    this._currentStep < STEPS.length - 1
                      ? html`
                        <span class="d-inline-block" data-next-tooltip>
                          <button
                            class="btn btn-primary"
                            ?disabled="${!this._canProceed}"
                            style="${!this._canProceed ? 'pointer-events: none' : ''}"
                            @click="${this._nextStep}"
                          >
                            Next: ${STEPS[this._currentStep + 1]?.label}
                          </button>
                        </span>
                      `
                      : html`
                        <span class="d-inline-block" data-next-tooltip>
                          <button
                            class="btn btn-success"
                            ?disabled="${this._creating || !this._canProceed}"
                            style="${this._creating || !this._canProceed ? 'pointer-events: none' : ''}"
                            @click="${this._createCharacter}"
                          >
                            ${this._creating ? html`<span class="spinner-border spinner-border-sm me-1"></span>` : ''}
                            ${this._creating ? 'Creating...' : 'Create Character'}
                          </button>
                        </span>
                      `
                  }
                </div>
              </div>
            `
            : ''
        }
      </div>
    `;
  }

  _renderCurrentStep() {
    switch (this._currentStep) {
      case 0:
        return this._renderClassStep();
      case 1:
        return this._renderBackgroundStep();
      case 2:
        return this._renderSpeciesStep();
      case 3:
        return this._renderAbilitiesStep();
      case 4:
        return this._renderEquipmentStep();
      case 5:
        return this._renderAlignmentStep();
      case 6:
        return this._renderDetailsStep();
      default:
        return '';
    }
  }

  _renderClassStep() {
    return html`
      <div>
        <div class="header-sentinel"></div>
        <class-step
          .classes="${this._classes}"
          selected-slug="${this._formData.classSlug}"
          .classOptions="${this._classOptions}"
          .skillProficiencies="${this._formData.skillProficiencies}"
          .featureChoices="${this._formData.featureChoices}"
          .equipmentPackageId="${this._formData.equipmentPackageId}"
          ?is-header-stuck="${this._isHeaderStuck}"
          @select-class="${(/** @type {CustomEvent} */ e) => this._selectClass(e.detail.slug)}"
          @toggle-skill="${(/** @type {CustomEvent} */ e) => this._toggleSkill(e.detail.slug)}"
          @select-choice-option="${(/** @type {CustomEvent} */ e) =>
            this._selectChoiceOption(e.detail.featureSlug, e.detail.choiceIndex, e.detail.optionSlug)}"
          @toggle-choice-option="${(/** @type {CustomEvent} */ e) =>
            this._toggleChoiceOption(
              e.detail.featureSlug,
              e.detail.choiceIndex,
              e.detail.optionSlug,
              e.detail.maxCount,
            )}"
          @select-equipment-package="${(/** @type {CustomEvent} */ e) => {
            this._formData = { ...this._formData, equipmentPackageId: e.detail.slug };
            this._saveState();
          }}"
        ></class-step>
      </div>
    `;
  }

  _renderBackgroundStep() {
    return html`
      <div>
        <div class="header-sentinel"></div>
        <background-step
          .backgrounds="${this._backgrounds}"
          selected-slug="${this._formData.backgroundSlug}"
          .backgroundOptions="${this._backgroundOptions}"
          .featOptions="${this._featOptions}"
          .backgroundEquipmentPackageId="${this._formData.backgroundEquipmentPackageId}"
          .backgroundToolChoice="${this._formData.backgroundToolChoice}"
          .featChoices="${this._formData.featChoices}"
          .replacementSkills="${this._formData.replacementSkills}"
          .classSkillProficiencies="${this._formData.skillProficiencies}"
          class-recommended-background="${this._classOptions?.class?.recommendedBackground || ''}"
          .alreadyChosenSpells="${this._getAlreadyChosen('spell', 'feat')}"
          origin-magic-initiate-spell-list="${this._originMagicInitiateSpellList || ''}"
          .featRecommendedSlugs="${this._featRecommendedSlugs}"
          .prefilledFeatChoices="${this._backgroundOptions?.feat?.prefilledChoices || {}}"
          .duplicateSkills="${this._duplicateSkills}"
          .availableReplacementSkills="${this._availableReplacementSkills}"
          ?is-header-stuck="${this._isHeaderStuck}"
          @select-background="${(/** @type {CustomEvent} */ e) => this._selectBackground(e.detail.slug)}"
          @select-equipment-package="${(/** @type {CustomEvent} */ e) => {
            this._formData = { ...this._formData, backgroundEquipmentPackageId: e.detail.slug };
            this._saveState();
          }}"
          @select-tool="${(/** @type {CustomEvent} */ e) => {
            this._formData = { ...this._formData, backgroundToolChoice: e.detail.tool };
            this._saveState();
          }}"
          @select-feat-choice="${(/** @type {CustomEvent} */ e) =>
            this._selectFeatChoice(e.detail.choiceId, e.detail.value)}"
          @toggle-feat-spell="${(/** @type {CustomEvent} */ e) =>
            this._toggleMultiSpell('featChoices', this._featOptions, e.detail.choiceId, e.detail.spellSlug)}"
          @toggle-replacement-skill="${(/** @type {CustomEvent} */ e) => this._toggleReplacementSkill(e.detail.slug)}"
        ></background-step>
      </div>
    `;
  }

  _renderSpeciesStep() {
    return html`
      <div>
        <div class="header-sentinel"></div>
        <species-step
          .speciesList="${this._species}"
          selected-slug="${this._formData.speciesSlug}"
          .speciesOptions="${this._speciesOptions}"
          .originFeats="${this._originFeats}"
          .originFeatOptions="${this._originFeatOptions}"
          species-size="${this._formData.speciesSize}"
          species-lineage="${this._formData.speciesLineage}"
          .speciesTraitChoices="${this._formData.speciesTraitChoices}"
          .originFeatChoices="${this._formData.originFeatChoices}"
          .languages="${this._formData.languages}"
          .alreadyChosenSkills="${this._getAlreadyChosen('skill')}"
          .alreadyChosenSpells="${this._getAlreadyChosen('spell', 'originFeat')}"
          background-magic-initiate-spell-list="${this._backgroundMagicInitiateSpellList || ''}"
          ?is-header-stuck="${this._isHeaderStuck}"
          @select-species="${(/** @type {CustomEvent} */ e) => this._selectSpecies(e.detail.slug)}"
          @select-lineage="${(/** @type {CustomEvent} */ e) => this._selectLineage(e.detail.slug)}"
          @select-trait-choice="${(/** @type {CustomEvent} */ e) =>
            this._selectTraitChoice(e.detail.key, e.detail.value)}"
          @select-origin-feat-choice="${(/** @type {CustomEvent} */ e) =>
            this._selectOriginFeatChoice(e.detail.choiceId, e.detail.value)}"
          @toggle-origin-feat-spell="${(/** @type {CustomEvent} */ e) =>
            this._toggleMultiSpell(
              'originFeatChoices',
              this._originFeatOptions,
              e.detail.choiceId,
              e.detail.spellSlug,
            )}"
          @toggle-language="${(/** @type {CustomEvent} */ e) => this._toggleLanguage(e.detail.slug)}"
          @select-size="${(/** @type {CustomEvent} */ e) => {
            this._formData = { ...this._formData, speciesSize: e.detail.size };
            this._saveState();
          }}"
        ></species-step>
      </div>
    `;
  }

  _renderAbilitiesStep() {
    return html`
      <ability-score-assigner
        .abilityScores="${this._formData.abilityScores}"
        class-slug="${this._formData.classSlug}"
        .backgroundAbilities="${this._backgroundAbilities}"
        background-name="${this._backgroundOptions?.background?.name || ''}"
        adjustment-method="${this._formData.abilityScoreAdjustmentMethod}"
        adjustment-primary="${this._formData.abilityScoreAdjustmentPrimary}"
        adjustment-secondary="${this._formData.abilityScoreAdjustmentSecondary}"
        .recommendedScores="${this._classOptions?.class?.defaultAbilityScores || null}"
        @change="${this._onAbilityAssignerChange}"
      ></ability-score-assigner>
    `;
  }

  /**
   * Class baseline weapon/armor proficiencies plus anything granted by the
   * player's currently-selected inline-option class-feature choices (e.g.
   * Cleric Divine Order -> Protector adds Martial Weapons + Heavy Armor).
   * Mirrors the merge applyInlineOptionProficiencies does on the backend so
   * the equipment step's "not proficient" warning matches what the saved
   * character will actually have.
   *
   * @returns {{weaponProficiencies: Array<string>, armorTraining: Array<string>}}
   */
  _effectiveProficiencies() {
    const weaponProficiencies = [...(this._classOptions?.coreTraits?.weaponProficiencies || [])];
    const armorTraining = [...(this._classOptions?.coreTraits?.armorTraining || [])];

    const WEAPON_LABELS = {
      simple: 'Simple Weapons',
      martial: 'Martial Weapons',
      'martial-light': 'Martial Light Weapons',
      'martial-finesse-or-light': 'Martial Finesse or Light Weapons',
    };
    const ARMOR_LABELS = {
      light: 'Light Armor',
      medium: 'Medium Armor',
      heavy: 'Heavy Armor',
      shield: 'Shields',
    };

    for (const feature of this._classOptions?.features || []) {
      if (!feature.choices) continue;
      for (let i = 0; i < feature.choices.length; i++) {
        const choice = /** @type {FeatureChoice} */ (feature.choices[i]);
        if (choice.choiceType !== 'option') continue;
        const selections = this._formData.featureChoices[`${feature.slug}:${i}`] || [];
        if (selections.length === 0) continue;
        const options = Array.isArray(choice.options) ? choice.options : [];
        for (const sel of selections) {
          const opt = /** @type {ChoiceOption | undefined} */ (
            options.find((o) => typeof o !== 'string' && o.slug === sel)
          );
          const prof = opt?.proficiencies;
          if (!prof) continue;
          for (const w of prof.weapons || []) {
            const label = WEAPON_LABELS[/** @type {keyof typeof WEAPON_LABELS} */ (w)];
            if (label && !weaponProficiencies.includes(label)) weaponProficiencies.push(label);
          }
          for (const a of prof.armor || []) {
            const label = ARMOR_LABELS[/** @type {keyof typeof ARMOR_LABELS} */ (a)];
            if (label && !armorTraining.includes(label)) armorTraining.push(label);
          }
        }
      }
    }

    return { weaponProficiencies, armorTraining };
  }

  _renderEquipmentStep() {
    const { weaponProficiencies, armorTraining } = this._effectiveProficiencies();
    return html`
      <div>
        <div class="header-sentinel"></div>
        <equipment-step
          .classEquipmentPackages="${this._classOptions?.equipmentPackages || []}"
          .backgroundEquipmentPackages="${this._backgroundOptions?.equipmentPackages || []}"
          .equipmentPackageId="${this._formData.equipmentPackageId}"
          .backgroundEquipmentPackageId="${this._formData.backgroundEquipmentPackageId}"
          .backgroundToolChoice="${this._formData.backgroundToolChoice}"
          .equipmentChanges="${this._formData.equipmentChanges}"
          .equipment="${this._equipment}"
          .weaponProficiencies="${weaponProficiencies}"
          .armorTraining="${armorTraining}"
          .weaponMasterySelections="${this._formData.featureChoices['weapon-mastery:0'] || []}"
          ?equipmentLoading="${this._equipmentLoading}"
          ?is-header-stuck="${this._isHeaderStuck}"
          @sell-item="${(/** @type {CustomEvent} */ e) => this._sellOneItem(e.detail.slug)}"
          @buy-item="${(/** @type {CustomEvent} */ e) => this._buyOneItem(e.detail.slug)}"
          @reset-changes="${this._resetEquipmentChanges}"
        ></equipment-step>
      </div>
    `;
  }

  _renderAlignmentStep() {
    return html`
      <div>
        <div class="header-sentinel"></div>
        <alignment-step
          selected-slug="${this._formData.alignment}"
          recommended-alignment="${this._classOptions?.class?.recommendedAlignment || ''}"
          ?is-header-stuck="${this._isHeaderStuck}"
          @select-alignment="${(/** @type {CustomEvent} */ e) => {
            this._formData = { ...this._formData, alignment: e.detail.slug };
            this._saveState();
          }}"
        ></alignment-step>
      </div>
    `;
  }

  _renderDetailsStep() {
    return html`
      <div>
        <div class="header-sentinel"></div>
        <details-step
          name="${this._formData.name}"
          token-id="${this._formData.tokenId}"
          .tokens="${this._tokens}"
          ?tokens-loading="${this._tokensLoading}"
          create-error="${this._createError || ''}"
          ?is-header-stuck="${this._isHeaderStuck}"
          @update-name="${(/** @type {CustomEvent} */ e) => {
            this._formData = { ...this._formData, name: e.detail.name };
            this._saveState();
          }}"
          @select-token="${(/** @type {CustomEvent} */ e) => {
            this._formData = { ...this._formData, tokenId: e.detail.id };
            this._saveState();
          }}"
          @randomize-name="${this._randomizeName}"
          @randomize-token="${this._randomizeToken}"
        ></details-step>
      </div>
    `;
  }
}

customElements.define('create-character-view', CreateCharacterView);
